import React, { useCallback, useState } from "react";
import { styled } from "@mui/material/styles";
import { Checkbox } from "@mui/material";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";

import { Component } from "interfaces/component";
import { CaseFilter as OptionType } from "interfaces/case-filter";
import { Autocomplete } from "components/Autocomplete";
import { useLazyGetComponents } from "hooks/components/useLazyGetComponents";
import { filterOptions } from "components/CasesTable/Filters/utils/filterOptions";

interface ComponentFilterProps {
  selectedComponents: Component[];
  onChangeSelectedComponents: (values: Component[]) => void;
  showChips?: boolean;
}

const selectAllOption: OptionType = {
  name: "Select All",
  ids: [0]
};

export const ComponentFilter = ({
  selectedComponents,
  onChangeSelectedComponents,
  showChips = true
}: ComponentFilterProps): JSX.Element => {
  const {
    loading: componentLoading,
    error: componentError,
    fetchComponents: getComponents
  } = useLazyGetComponents({});
  const [options, setOptions] = useState<OptionType[] | undefined>(undefined);
  const [selectedState, setSelectedState] = useState<OptionType[]>([]);
  const [inputValue, setInputValue] = useState("");

  const onFocus = useCallback(() => {
    /** If it is the first focus event, get options. */
    if (options === undefined) {
      getComponents().then(response => {
        if (response.results) {
          /** Component filter will combine as name */
          const formattedResult = response.results.reduce(
            (results: OptionType[], option): OptionType[] => {
              const duplicatedTargetIndex = results.findIndex(
                el => el.name === option.name
              );
              if (duplicatedTargetIndex !== -1) {
                results[duplicatedTargetIndex].ids = [
                  ...(results[duplicatedTargetIndex].ids || []),
                  option.id
                ];
              } else {
                results.push({
                  name: option.name,
                  ids: [option.id]
                });
              }
              return results;
            },
            []
          );
          setOptions([selectAllOption, ...formattedResult]);
        }
      });
    }
    setSelectedState(selectedComponents);
  }, [options, getComponents, selectedComponents]);

  return (
    <StyledAutocomplete
      label="Component"
      placeholder={showChips ? "Component" : ""}
      loading={componentLoading}
      error={componentError}
      showChips={showChips}
      size="small"
      multiple
      autoHighlight
      disableCloseOnSelect
      disableClearable
      autoComplete
      onFocus={onFocus}
      options={options ?? []}
      noOptionsText="No results found"
      getOptionLabel={(option: Component) => option.name}
      isOptionEqualToValue={(option: OptionType, value: OptionType) =>
        option.name === value.name
      }
      value={selectedComponents}
      onInputChange={(
        _event: React.ChangeEvent,
        newInputValue: string
      ): void => {
        if (inputValue !== newInputValue) {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={filterOptions}
      renderOption={(
        props: any,
        option: OptionType,
        { selected }: { selected: any }
      ) => {
        let { key, ...optionProps } = props;

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option =>
            selectedState.some(
              selected => selected.ids?.join("_") === option.ids?.join("_")
            )
        );

        const isSelected =
          option.name.toLowerCase() === "select all"
            ? isEqual(filteredSelectedState, filteredNonSelectAllOptions)
            : selected;

        optionProps = {
          ...optionProps,
          "aria-selected": isSelected
        };

        return (
          <li key={key} {...optionProps}>
            <Checkbox checked={isSelected} />
            {option.name}
          </li>
        );
      }}
      onChange={(_event: React.ChangeEvent, values: Component[]) => {
        if (!options?.length) {
          return;
        }

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option =>
            selectedState.some(
              selected => selected.ids?.join("_") === option.ids?.join("_")
            )
        );

        console.info("onChange > filteredSelectedState", filteredSelectedState);

        if (values.some(value => value.name.toLowerCase() === "select all")) {
          // If current selection includes "Select All"
          if (isEqual(filteredSelectedState, filteredNonSelectAllOptions)) {
            // If everything was selected, deselect all
            const selected = selectedState.filter(
              option =>
                !filteredSelectedState.some(
                  selected => selected.ids?.join("_") === option.ids?.join("_")
                )
            );
            onChangeSelectedComponents(selected as any);
            setSelectedState(selected);
          } else {
            const selected = uniqBy(
              [...selectedComponents, ...filteredNonSelectAllOptions],
              // @ts-ignore
              item => item.ids.join("_")
            );

            // If not everything was selected, select all
            onChangeSelectedComponents(selected as any);
            setSelectedState(selected);
          }
        } else {
          // Normal selection of individual items
          onChangeSelectedComponents(values);
          setSelectedState(values);
        }
      }}
    />
  );
};

const StyledAutocomplete = styled(Autocomplete)`
  height: 100%;
`;
